<div class="cabinet-tariffs">
  @if (pricingLoading()) {
    <p class="cabinet-tariffs__state">
      {{ 'CLIENT.CABINET.TARIFFS.LOADING' | translate }}
    </p>
  } @else if (pricingLoadError()) {
    <div class="cabinet-tariffs__state cabinet-tariffs__state--error">
      <p class="cabinet-tariffs__state-message" role="alert">
        {{ 'CLIENT.CABINET.TARIFFS.LOAD_ERROR' | translate }}
      </p>
      <lib-cabinet-button
        type="button"
        variant="secondary"
        secondaryTone="brand"
        size="md"
        [text]="'CLIENT.CABINET.TARIFFS.RETRY' | translate"
        [action]="{ type: 'action' }"
        (actionEvent)="retryLoadPricing()"
      />
      <lib-cabinet-service-status-hint />
    </div>
  } @else if (rates(); as rates) {
  <section
    class="cabinet-tariffs__benefits"
    [attr.aria-label]="'CLIENT.CABINET.TARIFFS.BENEFITS_ARIA' | translate"
  >
    <ul class="cabinet-tariffs__benefits-list">
      @for (item of benefitItems; track item.key) {
        <li class="cabinet-tariffs__benefit">
          <span class="cabinet-tariffs__benefit-icon" aria-hidden="true">
            <img [src]="item.iconSrc" alt="" width="20" height="20" />
          </span>
          <div class="cabinet-tariffs__benefit-copy">
            <span class="cabinet-tariffs__benefit-title">
              {{
                ('CLIENT.CABINET.TARIFFS.BENEFITS.' + item.key + '.TITLE')
                  | translate
              }}
            </span>
            <span class="cabinet-tariffs__benefit-text">
              {{
                ('CLIENT.CABINET.TARIFFS.BENEFITS.' + item.key + '.TEXT')
                  | translate
              }}
            </span>
          </div>
        </li>
      }
    </ul>
  </section>

  <article
    class="cabinet-tariffs__featured rate-card rate-card--featured rate-card--box"
    aria-labelledby="cabinet-tariffs-featured-title"
  >
    <div class="rate-card__layout">
      <div class="rate-card__content">
        <p class="rate-card__eyebrow">
          {{ 'CLIENT.CABINET.TARIFFS.FEATURED.EYEBROW' | translate }}
        </p>
        <p id="cabinet-tariffs-featured-title" class="rate-card__box-title">
          {{ 'CLIENT.CABINET.TARIFFS.FEATURED.BOX_TITLE' | translate }}
        </p>
        <div class="rate-card__pricing">
          <div class="rate-card__value-row rate-card__value-row--primary">
            <span class="rate-card__from">
              {{ 'CLIENT.CABINET.TARIFFS.FEATURED.FROM' | translate }}
            </span>
            <span class="rate-card__value rate-card__value--min">
              {{ formatPrice(rates.minDailyBynd) }}
            </span>
            <span class="rate-card__unit">
              {{ 'CLIENT.CABINET.TARIFFS.FEATURED.UNIT' | translate }}
            </span>
          </div>
        </div>
      </div>
      <div class="rate-card__visual" aria-hidden="true">
        <img
          class="rate-card__box-image"
          [src]="boxImageSrc"
          [alt]="'CLIENT.CABINET.TARIFFS.FEATURED.BOX_IMAGE_ALT' | translate"
          width="2404"
          height="1710"
          loading="lazy"
          decoding="async"
        />
      </div>
    </div>
  </article>

  <section
    class="cabinet-tariffs__panel"
    aria-labelledby="cabinet-tariffs-base-title"
  >
    <h2 id="cabinet-tariffs-base-title" class="cabinet-tariffs__panel-title">
      {{ 'CLIENT.CABINET.TARIFFS.BASE_RATE_TITLE' | translate }}
    </h2>

    <div class="cabinet-tariffs__panel-body">
      <p class="cabinet-tariffs__rate-highlight">
        {{
          'CLIENT.CABINET.TARIFFS.BASE_RATE_VALUE'
            | translate: { rate: formatPrice(rates.baseRateByndPerM3Day) }
        }}
      </p>
      <ul class="cabinet-tariffs__note-list">
        <li class="cabinet-tariffs__note">
          {{
            'CLIENT.CABINET.TARIFFS.RATE_MIN_VOLUME'
              | translate: { min: formatVolumeM3(rates.minVolumeM3) }
          }}
        </li>
        <li class="cabinet-tariffs__note">
          {{
            'CLIENT.CABINET.TARIFFS.DISCOUNT_NOTE'
              | translate: {
                maxDiscount: formatPercent(rates.maxStandardDiscountPercent)
              }
          }}
        </li>
      </ul>
    </div>
  </section>

  <section
    class="cabinet-tariffs__panel"
    aria-labelledby="cabinet-tariffs-discounts-title"
  >
    <h2 id="cabinet-tariffs-discounts-title" class="cabinet-tariffs__panel-title">
      {{ 'CLIENT.CABINET.TARIFFS.VOLUME_DISCOUNTS_TITLE' | translate }}
    </h2>

    <ul class="cabinet-tariffs__discount-list">
      @for (tier of volumeTiers(); track tier.minVolumeM3) {
        @if (tier.discountPercent > 0) {
          <li class="cabinet-tariffs__discount-row">
            <span class="cabinet-tariffs__tier-range">{{
              tier.rangeLabel
            }}</span>
            <span class="cabinet-tariffs__tier-badge">
              −{{ formatPercent(tier.discountPercent) }}
            </span>
          </li>
        }
      }
    </ul>
  </section>

  <section
    class="cabinet-tariffs__panel"
    aria-labelledby="cabinet-tariffs-service-rates-title"
  >
    @if (serviceRates(); as serviceRates) {
    <h2
      id="cabinet-tariffs-service-rates-title"
      class="cabinet-tariffs__panel-title"
    >
      {{ 'CLIENT.CABINET.TARIFFS.SERVICE_RATES_TITLE' | translate }}
    </h2>

    <div class="cabinet-tariffs__service-grid">
      <article class="cabinet-tariffs__service-card">
        <h3 class="cabinet-tariffs__service-title">
          {{ 'CLIENT.CABINET.TARIFFS.SERVICE_RATES.PICKUP.TITLE' | translate }}
        </h3>
        <p class="cabinet-tariffs__service-price">
          <span class="cabinet-tariffs__service-rate">
            {{
              'CLIENT.CABINET.TARIFFS.SERVICE_RATES.PICKUP.PRICE'
                | translate: { rate: formatPrice(serviceRates.pickupByndPerM3) }
            }}
          </span>
          <span class="cabinet-tariffs__service-unit">
            {{ 'CLIENT.CABINET.TARIFFS.SERVICE_RATES.UNIT' | translate }}
          </span>
        </p>
        <p class="cabinet-tariffs__service-min">
          {{
            'CLIENT.CABINET.TARIFFS.SERVICE_RATES.PICKUP.MIN_VOLUME'
              | translate: { min: formatVolumeM3(serviceRates.pickupMinVolumeM3) }
          }}
        </p>
        <p class="cabinet-tariffs__service-note">
          {{ 'CLIENT.CABINET.TARIFFS.SERVICE_RATES.PICKUP.NOTE' | translate }}
        </p>
      </article>

      <article class="cabinet-tariffs__service-card">
        <h3 class="cabinet-tariffs__service-title">
          {{ 'CLIENT.CABINET.TARIFFS.SERVICE_RATES.RETURN.TITLE' | translate }}
        </h3>
        <p class="cabinet-tariffs__service-price">
          <span class="cabinet-tariffs__service-rate">
            {{
              'CLIENT.CABINET.TARIFFS.SERVICE_RATES.RETURN.PRICE'
                | translate: { rate: formatPrice(serviceRates.returnByndPerM3) }
            }}
          </span>
          <span class="cabinet-tariffs__service-unit">
            {{ 'CLIENT.CABINET.TARIFFS.SERVICE_RATES.UNIT' | translate }}
          </span>
        </p>
        <p class="cabinet-tariffs__service-min">
          {{
            'CLIENT.CABINET.TARIFFS.SERVICE_RATES.RETURN.MIN_VOLUME'
              | translate: { min: formatVolumeM3(serviceRates.returnMinVolumeM3) }
          }}
        </p>
        <p class="cabinet-tariffs__service-note">
          {{ 'CLIENT.CABINET.TARIFFS.SERVICE_RATES.RETURN.NOTE' | translate }}
        </p>
      </article>

      <article class="cabinet-tariffs__service-card">
        <h3 class="cabinet-tariffs__service-title">
          {{ 'CLIENT.CABINET.TARIFFS.SERVICE_RATES.PACKAGING.TITLE' | translate }}
        </h3>
        <p class="cabinet-tariffs__service-price">
          <span class="cabinet-tariffs__service-rate">
            {{
              'CLIENT.CABINET.TARIFFS.SERVICE_RATES.PACKAGING.PRICE'
                | translate
                  : { rate: formatPrice(serviceRates.packagingByndPerM3) }
            }}
          </span>
          <span class="cabinet-tariffs__service-unit">
            {{ 'CLIENT.CABINET.TARIFFS.SERVICE_RATES.UNIT' | translate }}
          </span>
        </p>
        <p class="cabinet-tariffs__service-min">
          {{
            'CLIENT.CABINET.TARIFFS.SERVICE_RATES.PACKAGING.MIN_VOLUME'
              | translate
                : { min: formatVolumeM3(serviceRates.packagingMinVolumeM3) }
          }}
        </p>
        <p class="cabinet-tariffs__service-note">
          {{
            'CLIENT.CABINET.TARIFFS.SERVICE_RATES.PACKAGING.NOTE' | translate
          }}
        </p>
      </article>
    </div>

    <p class="cabinet-tariffs__service-footnote">
      {{ 'CLIENT.CABINET.TARIFFS.SERVICE_RATES_FOOTNOTE' | translate }}
    </p>
    }
  </section>

  <section
    class="cabinet-tariffs__panel cabinet-tariffs__panel--promo"
    [attr.aria-labelledby]="'cabinet-tariffs-promocode-heading'"
  >
    <div class="cabinet-tariffs__panel-body cabinet-tariffs__panel-body--promo">
      @if (promocode(); as promo) {
        <h2 id="cabinet-tariffs-promocode-heading" class="cabinet-tariffs__panel-title">
          {{ 'CLIENT.CABINET.WALLET.PROMOCODE.TITLE_APPLIED' | translate }}
        </h2>
        <p class="cabinet-tariffs__promo-value">
          {{
            'CLIENT.CABINET.WALLET.PROMOCODE.APPLIED'
              | translate: { code: promo.promocode, percent: promo.discount }
          }}
        </p>
        <p class="cabinet-tariffs__promo-hint">
          {{ 'CLIENT.CABINET.WALLET.PROMOCODE.APPLIED_HINT' | translate }}
        </p>
      } @else {
        <h2 id="cabinet-tariffs-promocode-heading" class="cabinet-tariffs__panel-title">
          {{ 'CLIENT.CABINET.WALLET.PROMOCODE.TITLE' | translate }}
        </h2>
        <p class="cabinet-tariffs__promo-lead">
          @if (maxPromoPercent(); as maxPromoPercent) {
            {{
              'CLIENT.CABINET.WALLET.PROMOCODE.LEAD'
                | translate: { maxPercent: maxPromoPercent }
            }}
          }
        </p>
      }

      <lib-cabinet-button
        type="button"
        class="cabinet-tariffs__promo-cta"
        variant="secondary"
        secondaryTone="brand"
        size="md"
        [fullWidth]="true"
        [text]="promocodeCtaKey() | translate"
        [action]="{ type: 'action' }"
        (actionEvent)="openPromocodeModal()"
      />
    </div>
  </section>
  }
</div>
